import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import lodashGet from 'lodash/get';
import lodashSet from 'lodash/set';
import Label from '@adsk/bim360-matrix-react-label';
import Editable from '@adsk/bim360-matrix-react-editable';
import {issueAttributesType, idToTitleMapType} from '../../constants/proptype-validation';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {fieldEdit} from '../../utils/analytics-helpers';
import IssueInfoPopover from '../issues/IssueInfoPopover';
import {hasPermittedAttribute} from '../../utils/issues-helpers';
import {trackEvent, updateIssueAction} from '../../app/actions';
import {getFieldIssueTypesAndSubTypesIdToTitleMap} from '../../app/selectors';
import {connect} from '../../app/connect';

class IssueFieldInlineEdit extends PureComponent {
  constructor(props) {
    super(props);
    const {issue} = this.props;

    this.state = {
      value: this.getValue(issue)
    };

    this.onUpdateSuccess = this.onUpdateSuccess.bind(this);
    this.onUpdateError = this.onUpdateError.bind(this);
  }

  componentWillMount() {
    const {issue} = this.props;

    this.updateState(issue);
  }

  componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.issue, nextProps.issue)) {
      this.updateState(nextProps.issue);
    }
  }

  getValue(issue) {
    const {attributeName} = this.props;
    return lodashGet(issue, attributeName);
  }

  updateState(issue) {
    this.setState({
      value: this.props.getFieldValue(issue, this.props)
    });
  }

  onUpdateError() {
    const {issue} = this.props;

    // Update state to original issue field value on update error
    this.updateState(issue);
  }

  onUpdateSuccess(updatedIssue) {
    const {issue, attributeName, dispatch, issueTypesAndSubTypesTitleMap} = this.props;

    // Track fieldEdit analytics event, assignee edit has a separate event (`assigneeChange`)
    // which is triggered from the middleware so we ignore it here
    if (
      lodashGet(issue, attributeName) !== lodashGet(updatedIssue, attributeName) &&
      attributeName !== ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO
    ) {
      const issueTypeTitle = issueTypesAndSubTypesTitleMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]];
      const issueSubTypeTitle = issueTypesAndSubTypesTitleMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]];
      dispatch(trackEvent(fieldEdit(attributeName, issueTypeTitle, issueSubTypeTitle)));
    }
  }

  updateIssueWrapper(closeEditMode) {
    return newValue => {
      const {issue, getIssueAttributes, dispatch} = this.props;
      dispatch(
        updateIssueAction(issue, getIssueAttributes(newValue, this.props), {
          onError: this.onUpdateError,
          onSuccess: this.onUpdateSuccess
        })
      );

      this.setState({
        value: newValue
      });
      closeEditMode();
    };
  }

  renderLabel() {
    const {label, container, infoMessage} = this.props;
    if (label || infoMessage) {
      return (
        <div className="IssueFieldInlineEdit__label-container">
          {label && <Label className="IssueFieldInlineEdit__label">{label}</Label>}
          {infoMessage && <IssueInfoPopover message={infoMessage} container={container} />}
        </div>
      );
    }
    return null;
  }

  render() {
    const {
      issue,
      attributeName,
      getDisplayValue,
      placeholder,
      className,
      children,
      disabled,
      styleType,
      linkify,
      multiline
    } = this.props;
    const currentValue = this.state.value;
    const divClassNames = classnames(`IssueFieldInlineEdit IssueFieldInlineEdit--${styleType}`, className);
    return (
      <div className={divClassNames}>
        {this.renderLabel()}
        <Editable
          data-testid={this.props['data-testid']}
          value={getDisplayValue(currentValue)}
          placeholder={placeholder}
          readOnly={disabled || !hasPermittedAttribute(issue, attributeName)}
          linkify={linkify}
          multiline={multiline}
        >
          {closeEditMode => children(currentValue, closeEditMode, this.updateIssueWrapper(closeEditMode))}
        </Editable>
      </div>
    );
  }
}

IssueFieldInlineEdit.displayName = 'IssueFieldInlineEdit';

IssueFieldInlineEdit.StyleTypes = {
  DEFAULT: 'default',
  SMALL: 'small'
};

IssueFieldInlineEdit.defaultProps = {
  styleType: IssueFieldInlineEdit.StyleTypes.DEFAULT,
  getDisplayValue: v => v,
  getFieldValue: (issue, {attributeName}) => lodashGet(issue, attributeName),
  getIssueAttributes: (value, {attributeName}) => lodashSet({}, attributeName, value)
};

IssueFieldInlineEdit.propTypes = {
  issue: issueAttributesType,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  attributeName: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  getDisplayValue: PropTypes.func,
  getFieldValue: PropTypes.func,
  getIssueAttributes: PropTypes.func,
  children: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  infoMessage: PropTypes.string,
  container: PropTypes.object,
  styleType: PropTypes.oneOf(Object.values(IssueFieldInlineEdit.StyleTypes)),
  linkify: PropTypes.bool,
  multiline: PropTypes.bool,
  issueTypesAndSubTypesTitleMap: idToTitleMapType.isRequired,
  'data-testid': PropTypes.string
};

const mapStateToProps = state => ({
  issueTypesAndSubTypesTitleMap: getFieldIssueTypesAndSubTypesIdToTitleMap(state)
});

export default connect(mapStateToProps)(IssueFieldInlineEdit);
